import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Popup, Text, XIcon, } from "@commonsku/styles";
import { CATEGORIES, POPUP_MAX_HEIGHT } from './helpers';
import ThemePreview from "./ThemePreview";
import TemplateTabs from "./TemplateTabs";

export default function SelectTemplatePopup({
  onClose,
  layouts,
  selectedTheme=null,
  selectedTemplate=null,
  initialFilters = [],
  initialCategory = CATEGORIES.all.key,
  onClickSelect,
  isShop,
  entity,
  zIndex = undefined,
  labelTitle = 'Shop',
}) {
  const companyName = entity.company_data?.tenant_name || '';

  const ref = useRef(null);
  const [popupHeight, setPopupHeight] = useState(300);
  const [selectedPreviewThemeIdx, setPreviewThemeIdx] = useState(-1);
  const [selectedFilters, setFilters] = useState(initialFilters);
  const [tabPosition, setTabPosition] = useState();

  const themesFiltered = useMemo(() => {
    if (initialCategory === 'all') {
      return layouts;
    }
    return [];
  }, [layouts, initialCategory]);

  const onToggleFilters = useCallback((value) => {
    setFilters(s => {
      let result = s;
      const idx = s.findIndex(v => v === value);
      if (idx > -1) {
        result = [
          ...s.slice(0, idx),
          ...s.slice(idx + 1),
        ];
      } else {
        result = Array.from(new Set(s));
      }
      return result;
    });
  }, []);

  function onClickPreview(theme) {
    const selectedLayout = layouts.find(t => t.public_view_template_id === theme.public_view_template_id);
    window.open(selectedLayout.image);
  }

  function handleClickSelect(theme) {
    if (onClickSelect) {
      onClickSelect(theme);
    }
    onClose && onClose();
  }

  useEffect(() => {
    const elem = ref.current;
    function updateHeight() {
      setPopupHeight(elem?.clientHeight || 300);
    }
    const mutationObserver = new MutationObserver(updateHeight);
    const resizeObserver = new ResizeObserver(updateHeight);
    if (elem) {
      mutationObserver.observe(elem, { childList: true, subtree: true });
      resizeObserver.observe(elem);
    }
    updateHeight();
    return () => {
      mutationObserver.disconnect();
      if (elem) {
        resizeObserver.unobserve(elem);
      } else {
        resizeObserver.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const tabElems = document.querySelector(
      '.popup.select-theme-popup .popup-content.select-theme-popup-content .template-tabs-wrapper > div > ul'
    )?.querySelectorAll('li');
    if ((tabElems?.length || 0) >= 2) {
      const companyTabPos = tabElems[0].getBoundingClientRect();
      const commonskuTabPos = tabElems[1].getBoundingClientRect();

      const tabCenter = companyTabPos.width + (commonskuTabPos.width / 2) + 24 + 15;
      setTabPosition({ center: tabCenter });
    }
  }, []);

  return (
    <Popup
      ref={ref}
      closeOnEsc
      noHeader
      popupClassName="select-theme-popup"
      contentClassName="select-theme-popup-content"
      style={{
        maxHeight: POPUP_MAX_HEIGHT,
        maxWidth: 1700,
        overflow: 'hidden',
        padding: 24,
        background: 'var(--color-neutrals-20)'
      }}
      zIndex={zIndex}
      overlayZIndex={zIndex ? zIndex - 1 : undefined}
    >
      <Text as="p" style={{ color: 'var(--color-neutrals-90)', fontFamily: 'var(--font-family-bold)', fontSize: 24, paddingLeft: '1rem' }}>
        Currently Selected: {selectedTemplate?.public_view_template_name || selectedTheme?.public_view_template_name} {selectedTemplate ? 'Template' : 'Layout'}
      </Text>
      <div>
        <XIcon
          style={{ position: 'absolute', right: 5, cursor: 'pointer', top: 5, zIndex: 9, }}
          onClick={onClose}
        />
        <div className="template-tabs-wrapper" style={{
          overflow: 'auto',
          height: '100%',
          width: '100%',
        }}>
          {selectedPreviewThemeIdx < 0 ?
            <TemplateTabs
              company_name={companyName}
              selectedTheme={selectedTheme}
              selectedTemplate={selectedTemplate}
              themesFiltered={themesFiltered}
              filters={selectedFilters}
              onClose={onClose}
              onToggleFilters={onToggleFilters}
              onClickSelect={handleClickSelect}
              onClickPreview={onClickPreview}
              isShop={isShop}
              tabPosition={tabPosition}
              labelTitle={labelTitle}
            /> : <ThemePreview
            theme={layouts[selectedPreviewThemeIdx]}
            maxHeight={popupHeight}
            isEditable={false}
            hasNext={selectedPreviewThemeIdx+1 <= layouts.length-1}
            hasPrev={selectedPreviewThemeIdx-1 >= 0}
            onGoBack={() => setPreviewThemeIdx(-1)}
            onSelectTheme={() => {
              handleClickSelect(layouts[selectedPreviewThemeIdx]);
            }}
            onClickNextTheme={() => {
              if (selectedPreviewThemeIdx+1 > layouts.length-1) {
                setPreviewThemeIdx(0);
                return;
              }
              setPreviewThemeIdx(selectedPreviewThemeIdx+1);
            }}
            onClickPrevTheme={() => {
              if (selectedPreviewThemeIdx-1 < 0) {
                setPreviewThemeIdx(layouts.length-1);
                return;
              }
              setPreviewThemeIdx(selectedPreviewThemeIdx-1);
            }}
          />}
        </div>
      </div>
    </Popup>
  );
}
